/**
 * Générateur — Automatisme : utiliser le théorème de Pythagore pour calculer
 * la longueur d'un côté d'un triangle rectangle.
 * Attendu ID : 1088 | Niveau : 4ème | Domaine : Espace et géométrie, géométrie plane
 * Rubrique : AUTOMATISME (créé par le professeur, gen neuf du 2026-09-24).
 *
 * ── D'où vient l'intitulé ─────────────────────────────────────────────────
 * Ni le BO 2025 ni la liste officielle des automatismes ne le portent : au
 * programme 2025, Pythagore est un OBJECTIF de 4e (cycle 4 l. 1041
 * « Connaitre le théorème de Pythagore, sa réciproque, sa contraposée ») et
 * le seul automatisme Pythagore est en 3e (l. 1098, écrire l'égalité, gen
 * 1243). Ligne servie, qui fait plafond : Eduscol, attendus de fin de 4e
 * (2019) l. 416-417 « Il sait calculer une longueur d'un côté d'un triangle
 * rectangle à partir de la connaissance des longueurs des deux autres
 * côtés. », exemple de réussite l. 420-427 (l'échelle de 13 m, pied à 5 m).
 *
 * ── Cadrage (sous-agent programme-maths 2026-09-24, décisions prof) ───────
 * Des TRIPLETS (le résultat tombe juste), côtés ≤ 30 sur les cartes du
 * triangle seul. CALCULATRICE AUTORISÉE sur les cartes de calcul (décision
 * prof 24/09 au soir : « regarde si on a besoin de calculatrice, n'oublie
 * pas élève de 4e » : 29², 24², √841 ne se font pas de tête en 4e ; la
 * calculatrice de l'écran a ² et √) ; pas sur « Quel calcul ? » ni « Le bon
 * triangle », qui ne calculent rien. Rappel brouillon IMPÉRATIF sur les six
 * formats depuis le 25/09 (prof : « brouillon impératif » sur l'échelle ;
 * règle par attendu du 23/09, donc partout). Les deux cas de
 * l'intitulé à parts égales : l'hypoténuse (somme des carrés) et un côté de
 * l'angle droit (différence). Figure EXACTE (décision prof 24/09 :
 * proportions vraies, échelle libre) : figTriangle.js (3e), sept
 * orientations, angle droit codé à n'importe quelle lettre, les longueurs
 * écrites sur les côtés, « ? » en orange sur le côté cherché ; « rectangle
 * en R » est écrit aussi dans l'énoncé, comme sur les fiches du prof.
 * SACRIFIÉ (fiche coup de pouce à venir) : le triple (5 ; 12 ; 13), jamais
 * tiré, qui est aussi l'échelle d'Eduscol ; ses multiples restent.
 *
 * ── Révision 2026-09-25 (retour prof, carte par carte) ───────────────────
 *   - Brouillon impératif (RAPPEL_BROUILLON, juste avant la question), sauf
 *     sur « Quel calcul ? » et « Le bon triangle » où rien ne se calcule
 *     (check du soir).
 *   - « Les étapes du calcul » : la rédaction complète est écrite au-dessus
 *     des lignes à compléter, « On sait que le triangle … est rectangle en ….
 *     Donc, d'après le théorème de Pythagore : » ; la consigne ne nomme plus
 *     le théorème, la rédaction le fait.
 *   - « Deux triangles » (deux_triangles_texte, clé RENOMMÉE) : plus de cases
 *     guidées, une seule saisie « Calcule RA », TR se calcule au brouillon
 *     (prof : « ici rédige au brouillon, donne la réponse directement »).
 *     L'étagère de 189 suit (pythagore_deux_triangles_texte).
 *
 * ── Cartes ────────────────────────────────────────────────────────────────
 *   « Quel calcul ? » (calcul_qcm_unique) : figure, deux longueurs, « Quel
 *     calcul donne EL ? » ; quatre calculs dont une racine d'une somme et une
 *     racine d'une différence (la forme ne trahit rien) : il faut repérer
 *     l'hypoténuse et choisir. Forme des automatismes Eduscol 2020 p. 33
 *     (« la démarche, pas la valeur »). Le QCM de l'attendu.
 *   « Calcule la longueur » (longueur_texte) : saisie du nombre, l'unité en
 *     suffixe.
 *   « Les étapes du calcul » (etapes_texte_trou_saisie) : la rédaction (« On
 *     sait que … est rectangle en …. Donc, d'après le théorème de
 *     Pythagore : »), puis les lignes du cours du prof (19Pyth1) : l'égalité,
 *     les nombres, puis des cases sur les résultats ; côté de l'angle droit
 *     cherché, la ligne « AR² = ⬚ − ⬚ » du cours ; jamais deux cases autour
 *     d'un « + » (l'ordre compterait faux).
 *   « L'échelle » (echelle_texte) : un mur vertical, un sol horizontal, une
 *     échelle (figure exacte, angle droit codé au pied du mur) ; on cherche la
 *     hauteur atteinte, la longueur de l'échelle ou la distance au mur, en
 *     mètres, une seule étape. L'échelle complète d'Eduscol (hauteur PUIS
 *     angle) demande le cosinus : 1090 s'en charge pour l'angle.
 *   « Le bon triangle » (bon_triangle_qcm_unique) : l'item DEPP début de 2de
 *     2025 q.16, un triangle rectangle et sa hauteur (trois triangles
 *     rectangles) : l'hypoténuse de l'un d'eux (venue de 189 le 24/09 au soir,
 *     décision prof : « pas de Pythagore dans 189 »).
 *   « Deux triangles » (deux_triangles_texte) : la fiche 7 du prof
 *     (« Appliquer le théorème de Pythagore (2) », TER puis TRA) : le côté
 *     commun par Pythagore dans le premier, puis un côté du second ; une
 *     seule saisie, le côté du second, le reste au brouillon (venue de 189
 *     comme la précédente).
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   somme_pour_difference (PRINCIPALE) : un côté de l'angle droit cherché, on
 *     ajoute quand même les carrés ;
 *   difference_pour_somme : l'hypoténuse cherchée, on retranche ;
 *   racine_oubliee : on s'arrête au carré (225 pour 15) ;
 *   sans_carres : on ajoute ou on retranche les longueurs (9 + 12) ;
 *   mauvais_triangle (DEPP q.16 : 7,4 %), cote_angle_droit_pour_hypotenuse
 *     (DEPP q.16 : 4,5 %).
 *
 * Hors : l'égalité seule (1243, 3e) ; racine non entière et valeur approchée
 * (151) ; « est-il rectangle ? » (1089) ; choisir le théorème (190).
 */
import {
    randInt, randomChoice, shuffle, pickLettresSains, tex,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, triangleRectangle, tourner, ORIENTATIONS } from '../../3eme/geometrie-plane/figTriangle';
import { svgEchelle } from './figContextes';

const ATTENDU_ID = 1088;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALCUL = 'Quel calcul ?';
const L_LONGUEUR = 'Calcule la longueur';
const L_ETAPES = 'Les étapes du calcul';
const L_ECHELLE = 'L\'échelle';
const L_BON = 'Le bon triangle';
const L_DEUX = 'Deux triangles';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const UNITES = ['cm', 'cm', 'mm', 'm'];
const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade', 'Inès', 'Noam'];
/** Calculatrice AUTORISÉE (décision prof 2026-09-24 au soir) : carrés de nombres à deux chiffres et racines carrées. */
const calculatrice = (payload) => ({ ...payload, contenu: { ...payload.contenu, calculatrice: true } });
// Rappel brouillon (règle prof du 2026-09-23, par attendu ; posé ici le 2026-09-25) : IMPÉRATIF sur
// les six formats, juste avant la question.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

/** Triplets pythagoriciens [a, b, c], c l'hypoténuse, côtés ≤ 30, sans le sacrifié. */
export const TRIPLETS = [
    [3, 4, 5], [6, 8, 10], [9, 12, 15], [12, 16, 20], [15, 20, 25], [18, 24, 30],
    [8, 15, 17], [7, 24, 25], [10, 24, 26], [20, 21, 29],
];
/** Le triple de la fiche coup de pouce (et de l'échelle d'Eduscol) : jamais tiré. */
export const SACRIFIE = [5, 12, 13];

/** Les cas (triplet, longueur cherchée) : genre 'hyp' (l'hypoténuse) ou 'cote' (un côté de l'angle droit). */
const casDe = (genre) => TRIPLETS.flatMap(([a, b, c]) => (genre === 'hyp'
    ? [{ a, b, c, x: c }]
    : [{ a, b, c, x: a }, { a, b, c, x: b }]));

const cote = (p, q) => [p, q].sort().join('');

/** Un triangle rectangle nommé, ses longueurs et le côté cherché ; un tirage sur deux cherche l'hypoténuse. */
const tirer = () => {
    const genre = randInt(0, 1) === 0 ? 'hyp' : 'cote';
    const { a, b, c, x } = randomChoice(casDe(genre));
    const lettres = pickLettresSains(POOL, 3);
    const droit = randInt(0, 2);
    const [ca, cb] = randInt(0, 1) === 0 ? [a, b] : [b, a];
    const scene = triangleRectangle({ lettres, droit, a: ca, b: cb, deg: randomChoice(ORIENTATIONS) });
    const D = lettres[droit]; const P = lettres[(droit + 1) % 3]; const Q = lettres[(droit + 2) % 3];
    const longueurs = { [cote(D, P)]: ca, [cote(D, Q)]: cb, [cote(P, Q)]: c };
    const hyp = cote(P, Q);
    const cherche = Object.keys(longueurs).find((k) => longueurs[k] === x);
    const donnes = shuffle(Object.keys(longueurs).filter((k) => k !== cherche));
    const unite = randomChoice(UNITES);
    const cotes = Object.keys(longueurs).map((k) => ({
        de: k[0], a: k[1], texte: k === cherche ? '?' : `${longueurs[k]} ${unite}`, cherche: k === cherche,
    }));
    const nom = lettres.join('');
    const aria = `Le triangle ${nom}, rectangle en ${D}, angle droit codé ; ${donnes.map((k) => `${k} = ${longueurs[k]} ${unite}`).join(', ')} ; ${cherche} est cherché.`;
    return {
        genre, triplet: [a, b, c], x, lettres, nom, D, hyp, longueurs, cherche, donnes, unite,
        figure: imgElement(svgTriangle({ ...scene, cotes }, aria), 'large'),
    };
};

const intro = (t) => `<p>Le triangle ${tex(t.nom)} est rectangle en ${tex(t.D)}, avec `
    + `${tex(`${t.donnes[0]} = ${t.longueurs[t.donnes[0]]}`)} ${t.unite} et ${tex(`${t.donnes[1]} = ${t.longueurs[t.donnes[1]]}`)} ${t.unite}.</p>`;

// ─── Carte « Quel calcul ? » (QCM) ─────────────────────────────────────────

const genCalcul = () => {
    const t = tirer();
    let bon; let faux;
    if (t.genre === 'hyp') {
        const [p, q] = t.donnes.map((k) => t.longueurs[k]);
        const [petit, grand] = p < q ? [p, q] : [q, p];
        bon = tex(`\\sqrt{${p}^{2} + ${q}^{2}}`);
        faux = [
            { v: tex(`\\sqrt{${grand}^{2} - ${petit}^{2}}`), code: 'difference_pour_somme' },
            { v: tex(`${p}^{2} + ${q}^{2}`), code: 'racine_oubliee' },
            { v: tex(`${p} + ${q}`), code: 'sans_carres' },
        ];
    } else {
        const h = t.longueurs[t.hyp]; const v = t.longueurs[t.donnes.find((k) => k !== t.hyp)];
        bon = tex(`\\sqrt{${h}^{2} - ${v}^{2}}`);
        faux = [
            { v: tex(`\\sqrt{${h}^{2} + ${v}^{2}}`), code: 'somme_pour_difference' },
            { v: tex(`${h}^{2} - ${v}^{2}`), code: 'racine_oubliee' },
            { v: tex(`${h} - ${v}`), code: 'sans_carres' },
        ];
    }
    // Pas de rappel brouillon : on choisit une écriture, rien ne se calcule (prof 2026-09-25).
    const texte = intro(t) + `<p><b>Quel calcul donne ${tex(t.cherche)} ?</b></p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_CALCUL), elements_additionnels: t.figure });
    payload._meta.params = { genre: t.genre, triplet: t.triplet, cherche: t.cherche, hyp: t.hyp, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte « Calcule la longueur » (saisie) ────────────────────────────────

const genLongueur = () => {
    const t = tirer();
    const texte = intro(t) + RAPPEL_BROUILLON + `<p><b>Calcule ${tex(t.cherche)}.</b></p>`;
    const payload = makeSaisie(String(t.x), texte, { ...opts(L_LONGUEUR), suffixe: t.unite, elements_additionnels: t.figure });
    payload._meta.params = { genre: t.genre, triplet: t.triplet, cherche: t.cherche, hyp: t.hyp, rep: t.x };
    return calculatrice(payload);
};

// ─── Carte « Les étapes du calcul » (saisie guidée) ────────────────────────

const genEtapes = () => {
    const t = tirer();
    const [l1, l2] = Object.keys(t.longueurs).filter((k) => k !== t.hyp).sort();
    const H = t.hyp; const X = t.cherche; const u = `\\text{ ${t.unite}}`;
    const val = (k) => (k === X ? `${k}^{2}` : `${t.longueurs[k]}^{2}`);
    const lignes = [`$${H}^{2} = ${l1}^{2} + ${l2}^{2}$`];
    if (t.genre === 'hyp') {
        const c = t.longueurs[H];
        lignes.push(`$${H}^{2} = ${val(l1)} + ${val(l2)}$`, `$${H}^{2} = {{${c * c}}}$`, `$${H} = {{${c}}}${u}$`);
    } else {
        const h = t.longueurs[H]; const k = t.longueurs[t.donnes.find((d) => d !== H)];
        // second chemin : la différence des carrés est le carré de la longueur cherchée
        if (h * h - k * k !== t.x * t.x) throw new Error(`gen_1088 : étapes ${t.triplet}`);
        lignes.push(`$${h}^{2} = ${val(l1)} + ${val(l2)}$`, `$${X}^{2} = {{${h * h}}} - {{${k * k}}}$`, `$${X}^{2} = {{${t.x * t.x}}}$`, `$${X} = {{${t.x}}}${u}$`);
    }
    // La rédaction complète (prof 25/09), écrite au-dessus des lignes à compléter : l'hypothèse puis le théorème, comme au cours.
    const redaction = `<p>On sait que le triangle ${tex(t.nom)} est rectangle en ${tex(t.D)}. Donc, d'après le théorème de Pythagore :</p>`;
    const texte = intro(t) + RAPPEL_BROUILLON + `<p><b>Complète le calcul de ${tex(X)}.</b></p>` + redaction;
    const payload = makeTexteTrou(lignes.join('\n'), texte, { ...opts(L_ETAPES), mode: 'saisie', elements_additionnels: t.figure });
    payload._meta.params = { genre: t.genre, triplet: t.triplet, cherche: X, hyp: H };
    return calculatrice(payload);
};

// ─── Carte « L'échelle » (saisie, contexte) ────────────────────────────────

/**
 * Échelles (en m) : d la distance du pied au mur, h la hauteur atteinte, L la
 * longueur ; cherche = les longueurs qu'on peut demander. Toutes vérifiées
 * d² + h² = L² au test ; (5 ; 12 ; 13) n'y est pas (sacrifié).
 */
export const ECHELLES = [
    { d: 1.5, h: 2, L: 2.5, cherche: ['h', 'L', 'd'] },
    { d: 3, h: 4, L: 5, cherche: ['h', 'L', 'd'] },
    { d: 6, h: 8, L: 10, cherche: ['h', 'L', 'd'] },
    { d: 4.5, h: 6, L: 7.5, cherche: ['h', 'L', 'd'] },
    { d: 1.8, h: 2.4, L: 3, cherche: ['h', 'L', 'd'] },
    { d: 2.4, h: 3.2, L: 4, cherche: ['h', 'L', 'd'] },
    { d: 3.6, h: 4.8, L: 6, cherche: ['h', 'L', 'd'] },
    { d: 0.7, h: 2.4, L: 2.5, cherche: ['h', 'L', 'd'] },
    { d: 1.4, h: 4.8, L: 5, cherche: ['h', 'L', 'd'] },
];
const CAS_ECHELLE = ECHELLES.flatMap((e) => e.cherche.map((cherche) => ({ ...e, cherche })));
const fr = (x) => String(x).replace('.', ',');

const genEchelle = () => {
    const e = randomChoice(CAS_ECHELLE);
    const prenom = randomChoice(PRENOMS);
    const m = { d: `${fr(e.d)} m`, h: `${fr(e.h)} m`, L: `${fr(e.L)} m` };
    m[e.cherche] = '?';
    let donnees; let question;
    if (e.cherche === 'h') {
        donnees = `${prenom} pose une échelle de ${fr(e.L)} m contre un mur vertical, sur un sol horizontal. Le pied de l'échelle est à ${fr(e.d)} m du mur.`;
        question = 'À quelle hauteur arrive le haut de l\'échelle ?';
    } else if (e.cherche === 'd') {
        donnees = `${prenom} pose une échelle de ${fr(e.L)} m contre un mur vertical, sur un sol horizontal. Le haut de l'échelle arrive à ${fr(e.h)} m de hauteur.`;
        question = 'À quelle distance du mur est le pied de l\'échelle ?';
    } else {
        donnees = `${prenom} veut poser une échelle contre un mur vertical, sur un sol horizontal : le pied à ${fr(e.d)} m du mur et le haut à ${fr(e.h)} m de hauteur.`;
        question = 'Quelle doit être la longueur de l\'échelle ?';
    }
    const texte = `<p>${donnees}</p>${RAPPEL_BROUILLON}<p><b>${question}</b></p>`;
    const figure = imgElement(svgEchelle({ d: e.d, h: e.h, mesures: m, cherche: e.cherche }), 'large');
    const payload = makeSaisie(fr(e[e.cherche]), texte, { ...opts(L_ECHELLE), suffixe: 'm', elements_additionnels: figure });
    payload._meta.params = { d: e.d, h: e.h, L: e.L, cherche: e.cherche };
    return calculatrice(payload);
};

// ─── Carte « Le bon triangle » (QCM, DEPP q.16) ────────────────────────────

const genBonTriangle = () => {
    const [A, B, C, T] = pickLettresSains(POOL, 4);
    // ABC rectangle en B (côtés de l'angle droit a et b), T le pied de la hauteur issue de B
    const a = 3 + Math.random() * 2; const b = a * (0.55 + Math.random() * 0.3);
    const base = [[0, 0], [a, 0], [0, b]];                               // B, A, C
    const [pB, pA, pC] = tourner(randInt(0, 1) === 1 ? base.map(([x, y]) => [-x, y]) : base, randomChoice([0, 30, 60, 150, 200, 250, 320]));
    const t = ((pB[0] - pA[0]) * (pC[0] - pA[0]) + (pB[1] - pA[1]) * (pC[1] - pA[1])) / ((pC[0] - pA[0]) ** 2 + (pC[1] - pA[1]) ** 2);
    const pT = [pA[0] + t * (pC[0] - pA[0]), pA[1] + t * (pC[1] - pA[1])];
    const scene = {
        points: { [A]: pA, [B]: pB, [C]: pC, [T]: pT }, triangle: [A, B, C],
        segments: [[A, B], [B, C], [C, A], [B, T]],
        carres: [{ sommet: B, voisins: [A, C] }, { sommet: T, voisins: [B, C] }],
    };
    const s = (x, y) => tex(`[${[x, y].sort().join('')}]`);
    const triangles = [
        { nom: `${B}${A}${C}`, bon: s(A, C), faux: [{ v: s(A, B), code: 'cote_angle_droit_pour_hypotenuse' }, { v: s(A, T), code: 'mauvais_triangle' }, { v: s(T, C), code: 'mauvais_triangle' }] },
        { nom: `${A}${T}${B}`, bon: s(A, B), faux: [{ v: s(A, T), code: 'cote_angle_droit_pour_hypotenuse' }, { v: s(B, T), code: 'cote_angle_droit_pour_hypotenuse' }, { v: s(A, C), code: 'mauvais_triangle' }] },
        { nom: `${C}${T}${B}`, bon: s(B, C), faux: [{ v: s(C, T), code: 'cote_angle_droit_pour_hypotenuse' }, { v: s(B, T), code: 'cote_angle_droit_pour_hypotenuse' }, { v: s(A, C), code: 'mauvais_triangle' }] },
    ];
    const q = randomChoice(triangles);
    const aria = `Le triangle ${A}${B}${C} rectangle en ${B} et sa hauteur [${B}${T}] : trois triangles rectangles.`;
    const texte = `<p>Sur la figure, le triangle ${tex(`${A}${B}${C}`)} est rectangle en ${tex(B)} et ${tex(T)} est le point de ${tex(`[${A}${C}]`)} tel que ${tex(`(${B}${T})`)} est perpendiculaire à ${tex(`(${A}${C})`)}.</p>`
        + `<p><b>Quelle est l'hypoténuse du triangle ${tex(q.nom)} ?</b></p>`;   // pas de rappel brouillon : rien ne se calcule (prof 2026-09-25)
    const payload = makeQcm([q.bon, ...q.faux.map((f) => f.v)], q.bon, texte, { ...opts(L_BON), elements_additionnels: imgElement(svgTriangle(scene, aria), 'large') });
    payload._meta.params = { triangle: q.nom, distracteurs: q.faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte « Deux triangles » (Pythagore puis Pythagore, fiche 7 du prof ; une seule saisie depuis le 2026-09-25) ──

const PRIMITIFS = [[3, 4, 5], [5, 12, 13], [8, 15, 17], [7, 24, 25], [20, 21, 29], [12, 35, 37], [9, 40, 41]];
/**
 * Les paires (premier triangle, second triangle) : l'hypoténuse du premier
 * est un côté de l'angle droit du second ; côtés entiers ≤ 90, le second
 * côté de l'angle droit du second triangle de 0,4 à 2,5 fois le côté commun ;
 * jamais le triangle (5 ; 12 ; 13) lui-même (le sacrifié).
 */
export const PAIRES_TRIANGLES = (() => {
    const tous = [];
    PRIMITIFS.forEach(([a, b, c]) => { for (let k = c === 13 ? 2 : 1; k * c <= 90; k++) tous.push([k * a, k * b, k * c]); });
    const out = [];
    tous.forEach((t1) => tous.forEach((t2) => {
        [0, 1].forEach((i) => {
            if (t2[i] !== t1[2]) return;
            const x = t2[1 - i];
            if (x < 0.4 * t1[2] || x > 2.5 * t1[2]) return;
            out.push({ e1: t1[0], e2: t1[1], commun: t1[2], x, hyp2: t2[2] });
        });
    }));
    return out;
})();

const genDeuxTriangles = () => {
    const p = randomChoice(PAIRES_TRIANGLES);
    const [E, T, R, A] = pickLettresSains(POOL, 4);
    const [ET, ER] = randInt(0, 1) === 0 ? [p.e1, p.e2] : [p.e2, p.e1];
    // E(0 ; 0), T(0 ; ET), R(ER ; 0) ; A sur la perpendiculaire à (RT) en R, du côté opposé à E
    const u = [ET / p.commun, ER / p.commun];
    let pts = [[0, 0], [0, ET], [ER, 0], [ER + u[0] * p.x, u[1] * p.x]];
    if (randInt(0, 1) === 1) pts = pts.map(([x, y]) => [-x, y]);
    pts = tourner(pts, randInt(-20, 20));
    const unite = randomChoice(['cm', 'mm', 'm']);
    const points = { [E]: pts[0], [T]: pts[1], [R]: pts[2], [A]: pts[3] };
    const nom = (x, y) => `${x}${y}`;
    const scene = {
        points, triangle: [E, T, R, A],
        segments: [[E, T], [E, R], [T, R], [R, A], [T, A]],
        carres: [{ sommet: E, voisins: [T, R] }, { sommet: R, voisins: [T, A] }],
        cotes: [
            { de: E, a: T, texte: `${ET} ${unite}` }, { de: E, a: R, texte: `${ER} ${unite}` }, { de: T, a: A, texte: `${p.hyp2} ${unite}` },
            { de: T, a: R, texte: '?', cherche: true }, { de: R, a: A, texte: '?', cherche: true },
        ],
    };
    const aria = `Deux triangles rectangles : ${E}${T}${R} rectangle en ${E}, ${T}${R}${A} rectangle en ${R}, le côté [${T}${R}] en commun.`;
    const texte = `<p>Le triangle ${tex(`${T}${E}${R}`)} est rectangle en ${tex(E)} et le triangle ${tex(`${T}${R}${A}`)} est rectangle en ${tex(R)}. `
        + `On donne ${tex(`${nom(E, T)} = ${ET}`)} ${unite}, ${tex(`${nom(E, R)} = ${ER}`)} ${unite} et ${tex(`${nom(T, A)} = ${p.hyp2}`)} ${unite}.</p>`
        + RAPPEL_BROUILLON + `<p><b>Calcule ${tex(nom(R, A))}.</b></p>`;
    // Une seule saisie (prof 25/09) : le côté commun se calcule au brouillon, on donne la réponse directement.
    const payload = makeSaisie(String(p.x), texte, { ...opts(L_DEUX), suffixe: unite, elements_additionnels: imgElement(svgTriangle(scene, aria), 'large') });
    payload._meta.params = { ...p, ET, ER };
    return calculatrice(payload);
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'triplets seulement, jamais (5 ; 12 ; 13) ; lettres saines, angle droit à n\'importe quelle lettre, sept orientations, côtés nommés dans l\'ordre alphabétique, tout en KaTeX',
        'figure exacte, longueurs écrites dessus, « ? » orange sur le côté cherché',
    ],
    calcul_qcm_unique: 'hypoténuse cherchée : difference_pour_somme √(grand² − petit²), racine_oubliee (la somme des carrés), sans_carres (la somme des longueurs) ; côté de l\'angle droit cherché : somme_pour_difference √(h² + c²) (PRINCIPALE), racine_oubliee, sans_carres ; toujours une racine d\'une somme et une racine d\'une différence ; quatre valeurs deux à deux différentes (inégalité triangulaire)',
    bon_triangle_qcm_unique: 'DEPP q.16 : mauvais_triangle (un morceau de l\'hypoténuse du grand triangle, ou l\'hypoténuse du grand triangle pour un petit), cote_angle_droit_pour_hypotenuse',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    calcul_qcm_unique: genCalcul,
    longueur_texte: genLongueur,
    etapes_texte_trou_saisie: genEtapes,
    echelle_texte: genEchelle,
    bon_triangle_qcm_unique: genBonTriangle,
    deux_triangles_texte: genDeuxTriangles,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', texte: 'Saisie', texte_trou_saisie: 'Saisie guidée' };
const buildFormats = (prefix) => Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
    .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'longueur', label: L_LONGUEUR, formatsList: buildFormats('longueur') },
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'echelle', label: L_ECHELLE, formatsList: buildFormats('echelle') },
    { id: 'bon_triangle', label: L_BON, formatsList: buildFormats('bon_triangle') },
    { id: 'deux_triangles', label: L_DEUX, formatsList: buildFormats('deux_triangles') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
